// Renders a Matrix message body: prefers the HTML `formatted_body` (subset:
// bold/italic/code/pre/links/lists/quotes/breaks), falls back to lightweight
// markdown in the plain `body`. Output is nested <Text> so it stays selectable
// and wraps naturally. No external deps.
import React, { useMemo } from 'react';
import { Linking, StyleSheet, TextStyle, StyleProp } from 'react-native';
import { Text } from './ui/Text';
import { makeStyles, useTheme } from './theme';

type Span = { text: string; b?: boolean; i?: boolean; code?: boolean; del?: boolean; href?: string; mention?: boolean };
// matrix.to links to a user (from our chips, Element pills, etc.) render as mention pills, not links.
const isUserLink = (href: string) => /matrix\.to\/#\/(@|%40)/i.test(href);

const ENT: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
function decode(s: string) {
  return s.replace(/&(#\d+|#x[0-9a-f]+|\w+);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isNaN(n) ? m : String.fromCodePoint(n);
    }
    return ENT[e.toLowerCase()] ?? m;
  });
}
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Markdown subset -> HTML subset (for events without formatted_body). */
function mdToHtml(md: string): string {
  let s = esc(md);
  s = s.replace(/```[a-z]*\n?([\s\S]*?)```/g, (_, c) => '<pre>' + c.replace(/\n$/, '') + '</pre>');
  s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/(^|[\s(])_([^_\n]+)_/g, '$1<em>$2</em>');
  s = s.replace(/~~([^~\n]+)~~/g, '<del>$1</del>');
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2">$1</a>');
  s = s.replace(/(^|[^"'>=])(https?:\/\/[^\s<]+)/g, '$1<a href="$2">$2</a>');
  s = s.replace(/(^|[\s(])@([\w.\-]+)/g, '$1<mention>@$2</mention>'); // typed @name → pill
  s = s.replace(/^\s*[-*•]\s+(.*)$/gm, '• $1');
  s = s.replace(/^&gt;\s?(.*)$/gm, '<blockquote>$1</blockquote>');
  return s.replace(/\n/g, '<br>');
}

/** HTML subset -> flat styled spans. Unknown tags are ignored (their text kept). */
function htmlToSpans(html: string): Span[] {
  const out: Span[] = [];
  const st = { b: 0, i: 0, code: 0, pre: 0, del: 0, skip: 0, mention: 0, href: [] as string[] };
  const push = (text: string) => {
    if (!text || st.skip > 0) return;
    const href = st.href[st.href.length - 1];
    const mention = st.mention > 0 || (!!href && isUserLink(href));
    let t = decode(text);
    if (mention && !t.startsWith('@')) t = '@' + t;
    out.push({ text: t, b: st.b > 0, i: st.i > 0, code: st.code > 0 || st.pre > 0, del: st.del > 0, href: mention ? undefined : href, mention });
  };
  const lists: number[] = []; // open lists, innermost last: -1 = bulleted, n = items numbered so far
  const re = /<\/?([a-zA-Z0-9-]+)([^>]*)>|([^<]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    if (m[3] !== undefined) { push(st.pre > 0 ? m[3] : m[3].replace(/\s*\n\s*/g, ' ')); continue; }
    const closing = m[0][1] === '/';
    const tag = m[1].toLowerCase();
    const d = closing ? -1 : 1;
    switch (tag) {
      case 'strong': case 'b': st.b += d; break;
      case 'em': case 'i': st.i += d; break;
      case 'code': st.code += d; break;
      case 'pre': st.pre += d; push('\n'); break;
      case 'del': case 's': case 'strike': st.del += d; break;
      case 'a': {
        if (closing) st.href.pop();
        else { const h = /href\s*=\s*"([^"]*)"|href\s*=\s*'([^']*)'/i.exec(m[2] || ''); st.href.push(decode(h?.[1] ?? h?.[2] ?? '')); }
        break;
      }
      case 'mention': st.mention += d; break;
      case 'br': push('\n'); break;
      case 'p': case 'div': case 'blockquote': case 'table': case 'tr':
        if (closing) push('\n'); else if (tag === 'blockquote') push('\n│ ');
        break;
      case 'ul': case 'ol':
        if (closing) { lists.pop(); push('\n'); } else lists.push(tag === 'ol' ? 0 : -1);
        break;
      case 'li': {
        if (closing) break;
        const top = lists.length ? lists[lists.length - 1] : -1;   // numbered lists count, bulleted don't
        if (top >= 0) { lists[lists.length - 1] = top + 1; push(`\n${top + 1}. `); } else push('\n• ');
        break;
      }
      case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6': st.b += d; push('\n'); break;
      case 'mx-reply': st.skip += d; break; // strip Matrix reply-fallback quotes
      default: break;
    }
  }
  // Tidy: drop blank edge spans, trim leading/trailing newlines.
  while (out.length && /^\s*$/.test(out[0].text)) out.shift();
  while (out.length && /^\s*$/.test(out[out.length - 1].text)) out.pop();
  if (out.length) {
    out[0] = { ...out[0], text: out[0].text.replace(/^\n+/, '') };
    const last = out[out.length - 1];
    out[out.length - 1] = { ...last, text: last.text.replace(/\n+$/, '') };
  }
  return out;
}

export function RichText({ html, text, style }: { html?: string; text: string; style?: StyleProp<TextStyle> }) {
  const theme = useTheme(); const s = useStyles();
  const spans = useMemo(() => htmlToSpans(html ?? mdToHtml(text)), [html, text]);
  if (!spans.length) return <Text style={style}>{text}</Text>;
  return (
    <Text style={style} selectable>
      {spans.map((sp, i) => (
        <Text
          key={i}
          style={[sp.b && s.b, sp.i && s.i, sp.code && s.code, sp.del && s.del, !!sp.href && s.link, sp.mention && s.mention]}
          onPress={sp.href ? () => Linking.openURL(sp.href!).catch(() => {}) : undefined}
        >
          {sp.text}
        </Text>
      ))}
    </Text>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  b: { fontWeight: '700' },
  i: { fontStyle: 'italic' },
  code: { fontFamily: theme.mono, backgroundColor: theme.surface2, fontSize: 13.5 },
  del: { textDecorationLine: 'line-through' },
  link: { color: theme.accent, textDecorationLine: 'underline' },
  mention: { color: theme.accent, fontWeight: '700', backgroundColor: theme.accentBg },
}));
